import React from 'react';
import has from 'lodash/has';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import take from 'lodash/take';
import { formatUrl } from 'url-lib';
import gettext from '@eb/gettext';

import { getDateTimeAsMoment, getFormattedDateTime } from '@eb/datetime';

import { EVENT_TYPE, EVENTS_TYPE } from '../../constants';
import { EVENT_AUTOCOMPLETE_AFFILIATE } from '../../constants/constants';
import { getTransformedContent } from './transformation';

import { getLocationInfo } from './venue';

import { Trending as TrendingSvg } from '@eb/eds-iconography';

const _parseEventsSuggestions = (suggestions) => {
    let eventSuggestions = map(
        suggestions,
        (
            {
                id,
                name,
                startDate,
                startTime,
                timezone,
                primaryVenue,
                url,
                ticketsBy,
                image,
            },
            index,
        ) => ({
            ticketsBy,
            value: `${EVENT_TYPE}/${id}`,
            content: name,
            imageUrl: image ? image.url : '',
            imageAlt: name,
            secondaryContent: getFormattedDateTime(
                getDateTimeAsMoment(startDate, startTime),
                false,
                timezone,
            ),
            tertiaryContent: getLocationInfo(
                primaryVenue ? primaryVenue.name : '',
                primaryVenue ? primaryVenue.address.localizedAreaDisplay : '',
            ),
            header: index === 0 ? gettext('Events') : null,
            showDivider: suggestions.length === index + 1 || index === 4,
            dividerVertSpacing: 2,
            url: formatUrl(url, { aff: EVENT_AUTOCOMPLETE_AFFILIATE }),
            isSquareImage: true,
        }),
    );

    eventSuggestions = take(eventSuggestions, 5);

    return eventSuggestions;
};

const _parseQuerySuggestions = (suggestions) => {
    let querySuggestions = map(suggestions, (completion, index) => ({
        value: completion,
        content: completion,
        iconType: <TrendingSvg />,
        header: index === 0 ? gettext('Popular') : null,
        showDivider: suggestions.length === index + 1 || index === 4,
        dividerVertSpacing: 2,
    }));

    querySuggestions = take(querySuggestions, 5);

    return querySuggestions;
};

const PARSE_MAP = {
    [EVENTS_TYPE]: _parseEventsSuggestions,
    query: _parseQuerySuggestions,
    event: _parseEventsSuggestions,
    tags: (tags) => tags,
    filters: (filters) => filters,
};

export const parseSuggestions = (response = {}) => {
    const { result } = getTransformedContent(
        'aKeyThatDoesNotMatterInThisContext',
        {
            response,
        },
    );
    const parsedResponse = reduce(
        result.response,
        (memo, value, key) => {
            if (key in PARSE_MAP) {
                const data = PARSE_MAP[key](value);

                // if we're parsing events, we need the unparsed version as well
                if (key === EVENTS_TYPE) {
                    const rawEvents = reduce(
                        value,
                        (acc, val) => ({
                            ...acc,
                            [val.id]: val,
                        }),
                        {},
                    );

                    return {
                        ...memo,
                        rawEvents,
                        [key]: data,
                    };
                }

                return {
                    ...memo,
                    [key]: data,
                };
            }

            return memo;
        },
        {},
    );

    return {
        ...parsedResponse,
    };
};

export const transformSearchIntoSuggestions = (response = {}) => {
    const { result } = getTransformedContent(
        'aKeyThatDoesNotMatterInThisContext',
        {
            response,
        },
    );

    let events = [];
    let tags = [];
    let filters = [];

    if (has(result, 'response.events.results')) {
        events = result.response.events.results;
    }

    if (
        has(result, 'response.events.aggs.organizertagsautocompleteAgg.buckets')
    ) {
        const aggregatedTags =
            result.response.events.aggs.organizertagsautocompleteAgg.buckets;

        const filteredTags = aggregatedTags.filter(
            ({ docCount }) => docCount > 5,
        );

        tags = filteredTags.map((tag) => ({
            value: tag.key,
            count: tag.docCount,
            display: tag.displayName,
        }));
    }

    if (has(result, 'response.events.aggs.tags.buckets')) {
        const aggregatedFilters = result.response.events.aggs.tags.buckets;
        const filteredFilters = aggregatedFilters.filter(
            ({ docCount, key }) => docCount > 5 && !key.includes('Sub'),
        );
        filters = filteredFilters.map((filter) => ({
            value: filter.key,
            count: filter.docCount,
            type: filter.key.includes('Category') ? 'tags' : 'format',
        }));
    }

    if (has(result, 'response.events.aggs.dates.buckets')) {
        const aggregatedDates = result.response.events.aggs.dates.buckets;
        const filteredDates = aggregatedDates.filter(
            ({ docCount, key }) => docCount > 5 && key === 'this_weekend',
        );
        filters = [
            ...filters,
            ...filteredDates.map((date) => ({
                value: date.key,
                count: date.docCount,
                type: 'dates',
            })),
        ];
    }

    return {
        events,
        tags,
        filters,
    };
};
